import React, { useRef } from 'react';
import { css } from '@emotion/css';
import ReactMarkdown from 'react-markdown';
import { Block } from '../../types/sprintLessonTypes';
import { createMarkdownComponents } from './markdown/MarkdownComponents';

type BlockRendererProps = {
  block: Block;
  onMcqSelection?: (blockId: string, selectedOption: string) => void;
};

const BlockRenderer: React.FC<BlockRendererProps> = ({ block, onMcqSelection }) => {
  // Create a ref to store citation mapping for sequential numbering
  const citationMapRef = useRef<Map<string, number>>(new Map());
  const blockContainerStyle = css`
    margin-bottom: 2rem;
  `;

  const markdownStyle = css`
    line-height: 1.6;
  `;

  const imageStyle = css`
    max-width: 100%;
    border-radius: 0.5rem;
    margin: 1rem 0;
  `;

  const mcqContainerStyle = css`
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-top: 2rem;
  `;

  const mcqOptionStyle = css`
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    padding: 1rem;
    margin-top: 0.75rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover {
      background-color: #f3f4f6;
    }
  `;

  // Skip rendering if block is not ready yet
  if (block.state !== 'success') {
    return (
      <div className={blockContainerStyle} key={block.index_key}>
        <p>Loading block content...</p>
      </div>
    );
  }

  switch (block.block_type) {
    case 'markdown_content':
      return (
        <div className={blockContainerStyle} key={block.index_key}>
          <div className={markdownStyle}>
            <ReactMarkdown
              components={createMarkdownComponents(citationMapRef.current)}
            >
              {block.content.raw_markdown}
            </ReactMarkdown>
          </div>
        </div>
      );
    case 'image_content':
      return (
        <div className={blockContainerStyle} key={block.index_key}>
          <img 
            src={block.content.url} 
            alt="Lesson illustration" 
            className={imageStyle} 
          />
        </div>
      );
    case 'mcq':
      return (
        <div className={mcqContainerStyle} key={block.index_key}>
          <h3>{block.content.question}</h3>
          {block.content.options.map((option: string, idx: number) => (
            <div 
              key={idx} 
              className={mcqOptionStyle}
              onClick={() => onMcqSelection && onMcqSelection(block.index_key, option)}
            >
              {option}
            </div>
          ))}
        </div>
      );
    case 'feedback':
      return (
        <div className={blockContainerStyle} key={block.index_key}>
          <h3>Feedback</h3>
          <p>{block.content.feedback}</p>
          <p>Rating: {block.content.rating_out_of_5}/5</p>
        </div>
      );
    default:
      return (
        <div key={block.index_key}>
          Unsupported block type: {block.block_type}
        </div>
      );
  }
};

export default BlockRenderer;
